import React from "react";
import { api, errMsg, unwrap } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMoney, formatDate, today } from "@/lib/utils";
import { TrendingUp, Wallet, Users, AlertTriangle, InfoIcon, GraduationCap, Percent } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { useAuth } from "@/lib/auth";
import { usePublicBranding, useLookupValues } from "@/lib/platformConfig";
import { useI18n } from "@/i18n";

const StatCard = ({ label, value, sub, icon: Icon, tone = "brand", testid }) => {
  const toneStyles = {
    brand: "bg-[var(--brand)] text-white",
    accent: "bg-[var(--accent)] text-white",
    surface: "bg-white text-[var(--text-primary)] border border-[var(--subtle)]",
  }[tone];
  return (
    <Card className={`${toneStyles} lift-card`} data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className={`text-[10px] uppercase tracking-widest font-semibold ${tone === "surface" ? "text-[var(--text-muted)]" : "text-white/60"}`}>
            {label}
          </div>
          <Icon className={`h-4 w-4 ${tone === "surface" ? "text-[var(--accent)]" : "opacity-70"}`} />
        </div>
        <div className={`font-display font-bold text-3xl mt-3 ${tone === "surface" ? "text-[var(--brand)]" : ""}`}>
          {value}
        </div>
        {sub && (
          <div className={`text-xs mt-1 ${tone === "surface" ? "text-[var(--text-muted)]" : "text-white/60"}`}>
            {sub}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const FUNNEL_ORDER = ["enquiry", "applied", "documents_pending", "shortlisted", "interview_scheduled", "offered", "fee_paid", "enrolled"];
const FUNNEL_COLORS = ["#575E54", "#1B3B36", "#2C524C", "#0284C7", "#D97706", "#C05621", "#9C4221", "#16A34A"];
const FUNNEL_COLOR_BY_STATUS = {
  enquiry: "#575E54",
  applied: "#1B3B36",
  documents_pending: "#2C524C",
  shortlisted: "#0284C7",
  interview_scheduled: "#D97706",
  offered: "#C05621",
  fee_paid: "#9C4221",
  enrolled: "#16A34A",
};

const alertText = (alert, t) => {
  if (alert.code === "attendance_below_threshold") {
    return t("dashboard.alerts.attendanceBelow", { percentage: alert.percentage });
  }
  if (alert.code === "outstanding_threshold") {
    return t("dashboard.alerts.outstandingThreshold", { amount: formatMoney(alert.amount_paise || 0) });
  }
  if (alert.code === "unmarked_sections") {
    return t("dashboard.alerts.unmarkedSections", { count: alert.count, total: alert.total });
  }
  return alert.text;
};

export default function PrincipalDashboard() {
  const { user } = useAuth();
  const branding = usePublicBranding();
  const admissionLookup = useLookupValues("admission_statuses");
  const { t, language } = useI18n();
  const schoolName = branding.data?.client_name || user?.institution?.name;
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["principal-dashboard"],
    queryFn: async () => unwrap(await api.get("/principal/dashboard")),
  });

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid md:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6 lg:p-8" data-testid="principal-dashboard-error">
        <Card className="border-[var(--error)]">
          <CardContent className="p-6 text-sm text-[var(--error)]">
            {t("dashboard.loadError")} {errMsg(error)}
          </CardContent>
        </Card>
      </div>
    );
  }

  const att = data?.attendance || {};
  const col = data?.collections || {};
  const funnel = data?.admissions_funnel || {};
  const alerts = data?.alerts || [];
  const lookupRows = admissionLookup.data?.values || [];
  const lookupByStatus = Object.fromEntries(
    lookupRows
      .filter((row) => typeof row?.value === "string" && row.value)
      .map((row) => [row.value, row]),
  );
  const lookupFunnelOrder = lookupRows
    .filter(
      (row) =>
        typeof row?.value === "string" &&
        row.value &&
        row?.metadata?.funnel === true,
    )
    .sort((a, b) => {
      const left = Number.isFinite(a.sort_order) ? a.sort_order : 0;
      const right = Number.isFinite(b.sort_order) ? b.sort_order : 0;
      return left - right;
    })
    .map((row) => row.value);
  const funnelOrder = lookupFunnelOrder.length ? lookupFunnelOrder : FUNNEL_ORDER;
  const funnelData = funnelOrder
    .map((status, index) => ({
      name:
        lookupByStatus[status]?.label || t(`dashboard.funnel.${status}`),
      value: funnel[status] || 0,
      fill:
        FUNNEL_COLOR_BY_STATUS[status] ||
        FUNNEL_COLORS[index % FUNNEL_COLORS.length],
    }))
    .filter((item) => item.value > 0);

  return (
    <div className="min-w-0 max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8 space-y-6" data-testid="principal-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] font-semibold">
            {schoolName}
          </div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)] mt-1">
            {t("dashboard.title")}
          </h1>
        </div>
        <div className="text-sm text-[var(--text-secondary)]">
          {formatDate(today(), language, { weekday: "long" })}
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a, i) => {
            const Icon = a.level === "warning" ? AlertTriangle : InfoIcon;
            const cls = a.level === "warning"
              ? "bg-[var(--warning-bg)] text-[var(--warning)]"
              : "bg-[var(--info-bg)] text-[var(--info)]";
            return (
              <div key={i} className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium ${cls}`}>
                <Icon className="h-4 w-4" /> {alertText(a, t)}
              </div>
            );
          })}
        </div>
      )}

      {/* KPI Bento */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          value={`${att.percentage || 0}%`}
          label={t("dashboard.attendanceToday")}
          sub={t("dashboard.attendanceSummary", {
            present: att.present_today || 0,
            absent: att.absent_today || 0,
            unmarked: att.unmarked_today || 0,
          })}
          icon={Percent}
          tone="brand"
          testid="stat-attendance"
        />
        <StatCard
          label={t("dashboard.collectionsToday")}
          value={formatMoney(col.today_paise || 0)}
          sub={`${t("dashboard.mtd")} ${formatMoney(col.mtd_paise || 0)}`}
          icon={Wallet}
          tone="accent"
          testid="stat-collections"
        />
        {/* Once the term calendar exists, what families owe by the fee plan
            (term balance); before that, unpaid invoices. */}
        <StatCard
          label={col.term_balance ? t("feePlan.totalDue") : t("dashboard.outstanding")}
          value={formatMoney((col.term_balance ? col.term_balance.due_now_paise : col.outstanding_paise) || 0)}
          sub={col.term_balance
            ? `${t("feePlan.upcoming")} ${formatMoney(col.term_balance.upcoming_paise || 0)}`
            : t("dashboard.pendingInvoices")}
          icon={TrendingUp}
          tone="surface"
          testid="stat-outstanding"
        />
        <StatCard
          label={t("dashboard.totalStudents")}
          value={att.total_students || 0}
          sub={t("dashboard.enrolled")}
          icon={Users}
          tone="surface"
          testid="stat-students"
        />
      </div>

      {/* Admissions funnel + KPI charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="min-w-0 xl:col-span-2 border-[var(--subtle)]">
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <GraduationCap className="h-4 w-4 text-[var(--accent)]" /> {t("dashboard.admissionsFunnel")}
              </span>
              <span className="text-xs font-normal text-[var(--text-muted)]">{t("dashboard.newToday", { count: data?.new_admissions_today || 0 })}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {funnelData.length === 0 ? (
              <div className="text-sm text-[var(--text-muted)]">{t("dashboard.noApplications")}</div>
            ) : (
              <ResponsiveContainer width="100%" height={260} minWidth={0}>
                <BarChart data={funnelData} layout="vertical" margin={{ left: 20 }}>
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11, textTransform: "capitalize" }} />
                  <Tooltip cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {funnelData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-[var(--subtle)]">
          <CardHeader>
            <CardTitle className="font-display text-lg">{t("dashboard.quickActions")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {[
              { label: t("dashboard.reviewApplications"), href: "/admissions" },
              { label: t("dashboard.composeCircular"), href: "/announcements" },
              { label: t("dashboard.generateInvoices"), href: "/fees" },
              { label: t("dashboard.viewStudents"), href: "/students" },
            ].map((a) => (
              <a key={a.href} href={a.href} className="block border border-[var(--subtle)] rounded-md p-3 hover:border-[var(--accent)] hover:bg-[var(--bg)] transition-colors">
                <div className="text-sm font-semibold">{a.label}</div>
              </a>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
